import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Package } from "lucide-react";
import { useRoterista } from "../context/RoteristaContext";
import {
  aguardandoEntregaService,
  AguardandoEntregaItem,
} from "../../../services/aguardandoEntregaService";
import { formatAguardandoEntregaOrderLabel } from "../../AguardandoEntrega/utils/formatDisplay";

const descricaoResumo = (descricao: string | null): string => {
  if (!descricao?.trim()) return "—";
  if (descricao.includes("\n")) {
    const linhas = descricao
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
    return linhas.slice(0, 3).join(" · ").slice(0, 60) || "—";
  }
  const [size, ...rest] = descricao.split(" - ");
  if (rest.length === 0) return descricao.slice(0, 50) || "—";
  return [size?.trim(), rest.join(" - ").slice(0, 35)]
    .filter(Boolean)
    .join(" · ");
};

interface PedidosAguardandoEntregaProps {
  /** Preenche a altura disponível (coluna sticky no layout estreito). */
  fillHeight?: boolean;
}

const PedidosAguardandoEntrega: React.FC<PedidosAguardandoEntregaProps> = ({
  fillHeight = false,
}) => {
  const { lojaId, entregaSseAvailable } = useRoterista();

  const { data, isLoading } = useQuery({
    queryKey: ["aguardando-entrega", lojaId, "pending"],
    queryFn: () =>
      aguardandoEntregaService.listar(lojaId!, undefined, undefined, "pending"),
    enabled: !!lojaId,
    refetchInterval: entregaSseAvailable ? false : 30_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
  });

  const items: AguardandoEntregaItem[] = data?.items ?? [];
  const prontosParaChamar = items.filter((i) => i.prontoParaEntrega).length;

  const shellClass = fillHeight
    ? "flex h-full min-h-0 flex-col rounded-lg bg-white p-4 shadow"
    : "rounded-lg bg-white p-6 shadow";

  if (isLoading) {
    return (
      <div className={shellClass}>
        <h3 className="mb-4 flex items-center gap-2 text-lg font-medium text-neutral-900">
          <Package size={20} />
          Pedidos aguardando entrega
        </h3>
        <div className="py-8 text-center text-neutral-500">Carregando...</div>
      </div>
    );
  }

  return (
    <div className={shellClass}>
      <h3 className="mb-1 flex shrink-0 flex-wrap items-center gap-2 text-lg font-medium text-neutral-900">
        <Package size={20} />
        Pedidos aguardando entrega
        {items.length > 0 && (
          <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-sm font-semibold text-amber-800">
            {items.length}
          </span>
        )}
        {prontosParaChamar > 0 && (
          <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-sm font-semibold text-emerald-800">
            {prontosParaChamar} pronto{prontosParaChamar !== 1 ? "s" : ""}
          </span>
        )}
      </h3>
      <p className="mb-4 shrink-0 text-xs text-neutral-500">
        Pedidos parciais aparecem esmaecidos (ex.: 1/3). Só é possível chamar
        entregador quando todas as pizzas estiverem prontas.
      </p>

      {items.length === 0 ? (
        <div className="py-8 text-center text-neutral-500">
          Nenhum pedido aguardando entrega no momento.
        </div>
      ) : (
        <div
          className={
            fillHeight
              ? "min-h-0 flex-1 overflow-auto"
              : "overflow-x-auto"
          }
        >
          <table className="w-full min-w-[400px] border-collapse text-left">
            <thead>
              <tr className="border-b border-neutral-200 bg-neutral-50">
                <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-600">
                  Pedido
                </th>
                <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-600">
                  Pronto
                </th>
                <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-600">
                  Cliente
                </th>
                <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-600">
                  Descrição
                </th>
                <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-600">
                  Montador
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.id}
                  className={`border-b border-neutral-100 transition-colors hover:bg-neutral-50/50 ${
                    item.prontoParaEntrega ? "" : "opacity-50"
                  }`}
                >
                  <td className="px-3 py-2">
                    <span
                      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-sm font-semibold tabular-nums ${
                        item.prontoParaEntrega
                          ? "bg-slate-100 text-slate-800"
                          : "bg-neutral-100 text-neutral-600"
                      }`}
                      title={`Pedido interno #${item.orderId}`}
                    >
                      {formatAguardandoEntregaOrderLabel(
                        item.displayId,
                        item.orderId
                      )}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-flex rounded px-2 py-0.5 text-xs font-bold tabular-nums ${
                        item.prontoParaEntrega
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-amber-100 text-amber-800"
                      }`}
                    >
                      {item.unidadesProntas}/{item.unidadesTotal}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-sm text-neutral-700">
                    {item.pedidoIntegracao?.customerName ?? "—"}
                  </td>
                  <td className="max-w-[200px] truncate px-3 py-2 text-sm text-neutral-600">
                    {descricaoResumo(item.descricao)}
                  </td>
                  <td className="px-3 py-2 text-sm text-neutral-600">
                    {item.montador?.nome ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PedidosAguardandoEntrega;
